import React from 'react';
import styled from 'styled-components';
import { t } from '@lingui/macro';
import type { Card as CardModel } from '../types';
import { Card } from './Card';
import { NAV_ARROW_CLIP } from './styles';

/**
 * Mobile single-card carousel (PRD §5, from `reference/card-ui.css`). The track
 * is translated by `--selectedCardIdx * (-100% + 48px)`; each slide fills the
 * track content width (the Track's 32px side padding gives the card its margins
 * and a ~16px peek of the neighbour), sized with `%` — not `100svw` — so a
 * scrollbar can't desync the slide width from the translate. Prev/next
 * arrows and dot indicators mutate the selected index (local state, never sent
 * to the backend — PRD §4 client-only state). Arrows/indicators are hidden for
 * a single card; prev hidden at index 0, next at the last index (PRD §9).
 */

const Viewport = styled.div`
  position: relative;
  overflow: hidden;
`;

const Track = styled.div<{ $idx: number }>`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 32px;
  transform: translateX(calc(${({ $idx }) => $idx} * (-100% + 48px)));
  transition: transform 0.3s ease-out;
`;

const Slide = styled.div`
  /* Full track content width (the Track's 32px side padding gives the card its
     margins). Uses % — not 100svw — so a vertical scrollbar can't desync the
     slide width from the -100% translate and drift later cards off-screen. */
  width: 100%;
  flex-shrink: 0;
`;

const NavArrow = styled.button<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${({ $side }) => ($side === 'left' ? 'left: 12px;' : 'right: 12px;')}
  transform: translateY(-50%);
  z-index: 2;
  width: 40px;
  height: 40px;
  min-width: 40px;
  padding: 10px;
  border: none;
  border-radius: 9999px;
  background: rgba(102, 112, 133, 0.6);
  backdrop-filter: blur(10px);
  opacity: 0.9;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  cursor: pointer;
  transition: opacity 0.2s, background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  /* Off-centre taps on the arrow's bounding-box corners could fall through to
     the full-card navigation overlay behind it (z-index 1), navigating to the
     card detail instead of advancing. An explicit full-rect transparent hit
     layer guarantees the corners belong to the button (clicks bubble up).
     Verified across all four corners + centre in Chrome. */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
  }

  &:hover {
    opacity: 1;
    background: rgba(102, 112, 133, 0.75);
  }
  &:active {
    background: rgba(102, 112, 133, 0.85);
  }
  &:focus-visible {
    outline: 2px solid #667085;
    outline-offset: 2px;
  }

  &::after {
    content: '';
    width: 13px;
    height: 13px;
    background: #ffffff;
    clip-path: ${NAV_ARROW_CLIP};
    ${({ $side }) => ($side === 'left' ? 'transform: rotate(180deg);' : '')}
  }
`;

const Indicators = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 6px;
  /* Only margin-top: the MobileTop flex gap already spaces the dots from the
     balance below, so a margin-bottom too would double it (legacy is 12px). */
  margin: 12px 0 0;
`;

const Dot = styled.span<{ $active: boolean }>`
  width: ${({ $active }) => ($active ? 12 : 6)}px;
  height: 6px;
  border-radius: 100px;
  background: ${({ $active }) => ($active ? '#000000' : '#d9d9d9')};
  transition: width 0.3s, background 0.3s;
`;

type Props = {
  cards: CardModel[];
  selectedIdx: number;
  onSelect: (idx: number) => void;
  onNavigate: (route: string) => void;
};

export const CardCarousel: React.FC<Props> = ({ cards, selectedIdx, onSelect, onNavigate }) => {
  const multiple = cards.length > 1;
  return (
    <div>
      <Viewport>
        {multiple && selectedIdx > 0 && (
          <NavArrow
            type="button"
            $side="left"
            aria-label={t`Previous card`}
            onClick={() => onSelect(selectedIdx - 1)}
          />
        )}
        <Track $idx={selectedIdx}>
          {cards.map((card, i) => (
            <Slide key={card.id}>
              <Card card={card} faded={i !== selectedIdx} onNavigate={onNavigate} />
            </Slide>
          ))}
        </Track>
        {multiple && selectedIdx < cards.length - 1 && (
          <NavArrow
            type="button"
            $side="right"
            aria-label={t`Next card`}
            onClick={() => onSelect(selectedIdx + 1)}
          />
        )}
      </Viewport>
      {multiple && (
        <Indicators>
          {cards.map((card, i) => (
            <Dot key={card.id} $active={i === selectedIdx} />
          ))}
        </Indicators>
      )}
    </div>
  );
};
